// ShareItineraryModal.tsx
import React, { useState, useRef } from 'react';
import { Share2, Copy, Check, X, ExternalLink } from 'lucide-react';
import { createPortal } from 'react-dom';
import type { Location } from '../../types/chat';

interface ShareItineraryModalProps {
  itinerary: any;
  locations: Location[];
  onClose: () => void;
}

export function ShareItineraryModal({ itinerary, locations, onClose }: ShareItineraryModalProps) {
  const [shareUrl, setShareUrl] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const qrImageRef = useRef<HTMLImageElement>(null);
  const [destinationPhotoUrl, setDestinationPhotoUrl] = useState('');


  console.log('ShareModal received itinerary:', itinerary);
  console.log('ShareModal days:', itinerary.days);

  /* const API_URL = import.meta.env.DEV 
  ? 'http://localhost:3001' // Use YOUR local server
  : 'https://ai-demos-trippy-ai-copilot-dev.vercel.app'; */

  const generateShareLink = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      // Debug the payload
      const payload = { 
        itinerary, 
        locations,
        userId: localStorage.getItem('tripperUserId') || 'anonymous'
      };
      
      console.log('[Share] Full payload:', payload);
      console.log('[Share] Payload size (bytes):', JSON.stringify(payload).length);
      console.log('[Share] Itinerary structure:', {
        hasId: !!itinerary.id,
        hasDestination: !!itinerary.destination,
        hasDays: !!itinerary.days,
        daysCount: itinerary.days?.length,
        hasTripDetails: !!itinerary.tripDetails
      });
      console.log('[Share] Locations count:', locations.length);
      console.log('[Share] Auth token:', localStorage.getItem('authToken')?.substring(0, 20) + '...');

      // Check for potential issues
      try {
        JSON.stringify(payload);
      } catch (circularErr) {
        console.error('[Share] Circular reference detected:', circularErr);
        setError('Invalid data structure');
        return;
      }

      const API_URL = import.meta.env.DEV 
        ? 'https://stagingapp.trippymate.ai' 
        : '';
      
      // Try with a minimal payload first
      const minimalPayload = {
        itinerary: {
          id: itinerary.id || `temp_${Date.now()}`,
          destination: itinerary.destination || itinerary.tripDetails?.destination || 'Unknown',
          days: itinerary.days ? itinerary.days.slice(0, 5) : [], // Limit days for testing
          startDate: itinerary.startDate || new Date().toISOString(),
          endDate: itinerary.endDate || new Date().toISOString()
        },
        locations: locations.slice(0, 10).map(loc => ({ // Limit locations
          id: loc.id,
          name: loc.name,
          position: {
            lat: typeof loc.position.lat === 'number' ? loc.position.lat : 0,
            lng: typeof loc.position.lng === 'number' ? loc.position.lng : 0
          },
          city: loc.city || 'Unknown'
        })),
        userId: localStorage.getItem('tripperUserId') || 'anonymous'
      };
      
      console.log('[Share] Sending minimal payload:', minimalPayload);
      
      const response = await fetch(`${API_URL}/api/share-itinerary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(minimalPayload) // Use minimal payload
      });

      const responseText = await response.text();
      console.log('[Share] Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[Share] Failed to parse response:', responseText);
        setError('Invalid response from server');
        return;
      }

      if (!response.ok) {
        console.error('[Share] Server error:', response.status, data);
        
        // Check for specific error patterns
        if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (response.status === 413) {
          setError('Data too large. Try sharing a shorter itinerary.');
        } else {
          setError(data.error || data.message || 'Failed to generate share link');
        }
        return;
      }
      
      if (data.shareId) {
        const shareUrl = data.shareUrl.startsWith('http') 
          ? data.shareUrl 
          : `${window.location.origin}${data.shareUrl}`;
        setShareUrl(shareUrl);
        setQrCodeUrl(`${API_URL}${data.qrCodeUrl}`);
        setIsQrLoading(true);
      } else {
        setError(data.error || 'No share ID returned');
      }
    } catch (err) {
      console.error('[Share] Client error:', err);
      setError('Failed to connect to server');
    } finally {
      setIsGenerating(false);
    }
  };

  /* const copyToClipboard = async () => {
    try {
      const fullUrl = `${window.location.origin}${shareUrl}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy');
    }
  }; */

  const copyToClipboard = async () => {
    try {
      const fullUrl = `${window.location.origin}${shareUrl}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return shareUrl; // or return fullUrl if you need the complete URL
    } catch (err) {
      setError('Failed to copy');
      return shareUrl; // Still return it even on copy failure
    }
  };

  // Enhanced WhatsApp sharing with Web Share API support
  const shareViaWhatsApp = async () => {
    const destination = itinerary.destination || itinerary.tripDetails?.destination || 'Travel';
    const text = `Check out my ${destination} itinerary!`;
    
    // Check if Web Share API is available and supports sharing files
    if (navigator.share && navigator.canShare) {
      try {
        // First, try to convert QR code to blob for sharing
        if (destinationPhotoUrl) {
          const response = await fetch(destinationPhotoUrl);
          const blob = await response.blob();
          const file = new File([blob], `${destination}-travel.jpg`, { type: 'image/jpeg' });
          
          // Check if we can share files
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `${destination} Itinerary`,
              text: `${text}\n\n${shareUrl}`,
              files: [file]
            });
            return;
          }
        }
        
        // If file sharing is not supported, just share text and URL
        await navigator.share({
          title: `${destination} Itinerary`,
          text: text,
          url: shareUrl
        });
        return;
      } catch (err) {
        console.log('Web Share API failed, falling back to WhatsApp URL');
      }
    }
    
    // Fallback to WhatsApp URL scheme (text only)
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`);
  };

  // Generic share function using Web Share API
  const shareNative = async () => {
    const destination = itinerary.destination || itinerary.tripDetails?.destination || 'Travel';
    const text = `Check out my ${destination} itinerary!`;
    
    if (navigator.share) {
      try {
        // Try to share with image if possible
        if (qrImageRef.current && navigator.canShare) {
          const response = await fetch(qrCodeUrl);
          const blob = await response.blob();
          const file = new File([blob], `${destination}-itinerary-qr.png`, { type: 'image/png' });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `${destination} Itinerary`,
              text: `${text}\n\n${shareUrl}`,
              files: [file]
            });
            return;
          }
        }
        
        // Fallback to text/URL only
        await navigator.share({
          title: `${destination} Itinerary`,
          text: text,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          setError('Sharing failed');
        }
      }
    } else {
      setError('Sharing is not supported on this device');
    }
  };

  React.useEffect(() => {
  if (!itinerary.destination && !itinerary.tripDetails?.destination) return;
  
  const destination = itinerary.destination || itinerary.tripDetails?.destination;
  const cityName = destination.split(',')[0].trim();
  
  const placesService = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  placesService.findPlaceFromQuery({
    query: cityName,
    fields: ['place_id', 'name', 'photos']
  }, (results, status) => {
    if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
      const place = results[0];
      
      if (place.photos && place.photos.length > 0) {
        const photoUrl = place.photos[0].getUrl({
          maxWidth: 1200,
          maxHeight: 630 // WhatsApp preview dimensions
        });
        setDestinationPhotoUrl(photoUrl);
      }
    }
  });
}, [itinerary]);
  

  React.useEffect(() => {
    generateShareLink();
  }, []);

  const expandedContent = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold">Share Itinerary</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isGenerating ? (
            <div className="flex flex-col items-center py-8">
              {/* Animated Loading State */}
              <div className="relative mb-8">
                {/* Outer spinning ring */}
                <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-pulse"></div>
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                
                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Share2 size={24} />
                  {/* className="text-blue-600 animate-pulse" */}
                </div>
              </div>
              
              {/* Loading text with dots animation */}
              <p className="text-gray-600 text-lg">
                Generating share link
                <span className="inline-flex">
                  <span className="animate-bounce animation-delay-0">.</span>
                  <span className="animate-bounce animation-delay-150">.</span>
                  <span className="animate-bounce animation-delay-300">.</span>
                </span>
              </p>
              
              {/* Progress bar */}
              {/* <div className="w-full max-w-xs mt-4">
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full"
                    style={{
                      animation: 'progress 2s ease-in-out infinite',
                      width: '0%'
                    }}
                  ></div>
                </div>
              </div> */}
            </div>
          ) : error ? (
            <div className="text-red-600 text-center py-8">{error}</div>
          ) : (
            <>
              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-xl shadow-lg relative">
                  {isQrLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white rounded-xl">
                      {/* QR Code Skeleton Loader */}
                      <div className="w-48 h-48 relative">
                        {/* Grid pattern animation */}
                        <div className="grid grid-cols-3 gap-2 p-4">
                          {[...Array(9)].map((_, i) => (
                            <div
                              key={i}
                              className="bg-gray-200 rounded animate-pulse"
                              style={{
                                animationDelay: `${i * 100}ms`,
                                aspectRatio: '1'
                              }}
                            ></div>
                          ))}
                        </div>
                        {/* Loading text overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white/90 px-3 py-1 rounded-full">
                            <p className="text-sm text-gray-600 font-medium">Loading QR...</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <img 
                    ref={qrImageRef}
                    src={qrCodeUrl} 
                    alt="QR Code" 
                    className="w-48 h-48"
                    crossOrigin="anonymous" // Important for converting to blob
                    onLoad={() => setIsQrLoading(false)}
                    onError={() => {
                      setIsQrLoading(false);
                      setError('Failed to load QR code');
                    }}
                  />
                </div>
              </div>

              {/* Share URL */}
              <div className="mb-6">
                <label className="text-sm text-gray-600 mb-2 block">Share Link</label>
                <div className="flex gap-2">
                  {/* <button
                    onClick={() => window.open(shareUrl, '_blank')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <ExternalLink size={16} />
                    Open Link
                  </button> */}
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    {copied ? <Check size={16} /> : <ExternalLink size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Share Options */}
              <div className="space-y-3">
                {/* Show native share button on mobile devices */}
                {/* {navigator.share && (
                  <button
                    onClick={shareNative}
                    className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 size={20} />
                    Share Itinerary
                  </button>
                )} */}

                <button
                  onClick={shareViaWhatsApp}
                  className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Share on WhatsApp
                </button>

                <button
                  onClick={() => {
                    const destination = itinerary.destination || itinerary.tripDetails?.destination || 'Travel';
                    window.open(`mailto:?subject=${encodeURIComponent(`${destination} Itinerary`)}&body=${encodeURIComponent(`Check out my travel itinerary: ${shareUrl}`)}`);
                  }}
                  className="w-full py-3 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Share via Email
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                This link will expire in 30 days
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* Add styles */}
      <style>{`
        @keyframes progress {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
  
  return createPortal(expandedContent, document.body);
}

// Add share button to your ItineraryPanel
export function ShareButton({ itinerary, locations }: { itinerary: any, locations: Location[] }) {
  
  console.log('[Share] Itinerary being shared:', itinerary);
  console.log('[Share] Itinerary ID:', itinerary.id);
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Share itinerary"
      >
        <Share2 size={20} />
      </button>

      {showModal && (
        <ShareItineraryModal
          itinerary={itinerary}
          locations={locations}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}